import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { User, Package, MapPin, Lock, LogOut, Eye, EyeOff, Plus, Edit, Trash2, Star, ChevronRight, Download } from "lucide-react";

const TABS = [
  { key: "profile", label: "Mes informations", icon: User },
  { key: "orders", label: "Mes commandes", icon: Package },
  { key: "addresses", label: "Mes adresses", icon: MapPin },
  { key: "security", label: "Sécurité", icon: Lock },
];

function ProfileTab() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ first_name: user?.first_name || "", last_name: user?.last_name || "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.put("/auth/profile", form);
      updateUser({ first_name: r.data.first_name, last_name: r.data.last_name, name: r.data.name });
      toast.success("Informations mises à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="profile-tab">
      <h2 className="font-serif-display text-2xl mb-6">Mes informations</h2>
      <form onSubmit={submit} className="space-y-4 max-w-lg">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-[#6B655C] mb-1.5">Prénom</label>
            <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="profile-first-name" />
          </div>
          <div>
            <label className="block text-sm text-[#6B655C] mb-1.5">Nom</label>
            <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="profile-last-name" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-[#6B655C] mb-1.5">Adresse e-mail</label>
          <input disabled value={user?.email || ""} className="w-full p-3 rounded-2xl border border-[#E5E1D8] bg-[#faf8f4] text-[#6B655C] cursor-not-allowed" />
          <p className="text-xs text-[#6B655C] mt-1">L'adresse e-mail ne peut pas être modifiée</p>
        </div>
        <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50" data-testid="profile-save-btn">
          {loading ? "Enregistrement..." : "Enregistrer"}
        </button>
      </form>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { api.get("/orders/mine").then(r => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const statusLabel = (s) => {
    const map = { pending: "En attente", initiated: "Initiée", completed: "Confirmée", paid: "Payée", preparing: "En préparation", shipped: "Expédiée", delivered: "Livrée" };
    return map[s] || s;
  };
  const statusColor = (s) => {
    if (s === "paid" || s === "completed") return "bg-patchwork/20 text-patchwork";
    if (s === "preparing") return "bg-amber-100 text-amber-700";
    if (s === "shipped") return "bg-blue-100 text-blue-700";
    if (s === "delivered") return "bg-green-100 text-green-700";
    return "bg-[#E5E1D8] text-[#6B655C]";
  };

  if (loading) return <p className="text-[#6B655C]">Chargement...</p>;

  return (
    <div data-testid="orders-tab">
      <h2 className="font-serif-display text-2xl mb-6">Mes commandes</h2>
      {orders.length === 0 ? (
        <p className="text-[#6B655C]">Vous n'avez pas encore passé de commande.</p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const displayStatus = o.shipping_status || o.payment_status || o.status;
            return (
            <div key={o.id} className="bg-white/60 border border-[#E5E1D8] rounded-2xl overflow-hidden" data-testid="order-card">
              <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="w-full p-4 flex items-center justify-between text-left hover:bg-white/40 transition">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-[#1F1C18]">Commande du {new Date(o.created_at).toLocaleDateString("fr-FR")}</span>
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${statusColor(displayStatus)}`}>{statusLabel(displayStatus)}</span>
                  </div>
                  <p className="text-sm text-[#6B655C] mt-0.5">{o.items?.length || 0} article(s) — {o.amount?.toFixed(2)} €</p>
                </div>
                <ChevronRight size={18} className={`text-[#6B655C] transition-transform ${expanded === o.id ? "rotate-90" : ""}`} />
              </button>
              {expanded === o.id && (
                <div className="px-4 pb-4 border-t border-[#E5E1D8]">
                  <div className="pt-3 space-y-2">
                    {(o.items || []).map((it, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-[#1F1C18]">{it.quantity}× {it.name}</span>
                        <span className="text-[#6B655C]">{(it.price * it.quantity).toFixed(2)} €</span>
                      </div>
                    ))}
                    {o.discount?.applied && (
                      <div className="flex justify-between text-sm text-patchwork">
                        <span>{o.discount.label}</span>
                        <span>-{o.discount.amount?.toFixed(2)} €</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-medium pt-2 border-t border-[#E5E1D8]">
                      <span>Total</span>
                      <span className="text-terracotta">{o.amount?.toFixed(2)} €</span>
                    </div>
                    {o.shipping && (
                      <p className="text-xs text-[#6B655C] pt-1">
                        Livraison : {o.shipping.name} — {o.shipping.address}, {o.shipping.postal} {o.shipping.city}
                      </p>
                    )}
                    {o.tracking_number && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl" data-testid="tracking-info">
                        <p className="text-sm font-medium text-blue-800 mb-1">Suivi Colissimo</p>
                        <p className="text-sm text-blue-700 font-mono">{o.tracking_number}</p>
                        <a
                          href={`https://www.laposte.fr/outils/suivre-vos-envois?code=${o.tracking_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block mt-2 text-sm text-blue-600 underline hover:text-blue-800"
                          data-testid="tracking-link"
                        >
                          Suivre mon colis sur La Poste
                        </a>
                      </div>
                    )}
                    {(o.payment_status === "paid" || o.payment_status === "completed" || o.shipping_status === "shipped" || o.shipping_status === "delivered") && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const resp = await api.get(`/orders/${o.id}/invoice`, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `facture-labounite-${o.id.slice(0, 8)}.pdf`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          } catch {
                            toast.error("Erreur lors du téléchargement de la facture");
                          }
                        }}
                        className="mt-3 flex items-center gap-2 text-sm text-terracotta hover:text-terracotta/80 transition"
                        data-testid="download-invoice-btn"
                      >
                        <Download size={15} />
                        Télécharger la facture (PDF)
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

function AddressesTab() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "Domicile", first_name: "", last_name: "", address: "", postal: "", city: "", phone: "", is_default: false });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/addresses").then(r => setAddresses(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ label: "Domicile", first_name: "", last_name: "", address: "", postal: "", city: "", phone: "", is_default: false }); setEditing(null); setShowForm(false); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/addresses/${editing}`, form);
        toast.success("Adresse modifiée");
      } else {
        await api.post("/addresses", form);
        toast.success("Adresse ajoutée");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette adresse ?")) return;
    try {
      await api.delete(`/addresses/${id}`);
      toast.success("Adresse supprimée");
      load();
    } catch { toast.error("Erreur"); }
  };

  const startEdit = (a) => {
    setForm({ label: a.label, first_name: a.first_name, last_name: a.last_name, address: a.address, postal: a.postal, city: a.city, phone: a.phone || "", is_default: a.is_default });
    setEditing(a.id);
    setShowForm(true);
  };

  if (loading) return <p className="text-[#6B655C]">Chargement...</p>;

  return (
    <div data-testid="addresses-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif-display text-2xl">Mes adresses</h2>
        {!showForm && <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary text-sm flex items-center gap-1.5" data-testid="add-address-btn"><Plus size={16} /> Ajouter</button>}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white/60 border border-[#E5E1D8] rounded-2xl p-5 space-y-3 mb-6" data-testid="address-form">
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Libellé (ex: Domicile, Bureau)" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta text-sm" data-testid="address-label" />
          <div className="grid grid-cols-2 gap-3">
            <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Prénom" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta text-sm" data-testid="address-first-name" />
            <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta text-sm" data-testid="address-last-name" />
          </div>
          <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta text-sm" data-testid="address-street" />
          <div className="grid grid-cols-2 gap-3">
            <input required value={form.postal} onChange={(e) => setForm({ ...form, postal: e.target.value })} placeholder="Code postal" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta text-sm" data-testid="address-postal" />
            <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta text-sm" data-testid="address-city" />
          </div>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone (optionnel)" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta text-sm" data-testid="address-phone" />
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[#6B655C]">
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="accent-terracotta" data-testid="address-default" />
            Adresse par défaut
          </label>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary text-sm disabled:opacity-50" data-testid="address-save-btn">{saving ? "..." : editing ? "Modifier" : "Ajouter"}</button>
            <button type="button" onClick={resetForm} className="text-sm text-[#6B655C] hover:text-terracotta">Annuler</button>
          </div>
        </form>
      )}

      {addresses.length === 0 && !showForm ? (
        <p className="text-[#6B655C]">Aucune adresse enregistrée.</p>
      ) : (
        <div className="space-y-3">
          {addresses.map((a) => (
            <div key={a.id} className="bg-white/60 border border-[#E5E1D8] rounded-2xl p-4 flex justify-between items-start" data-testid="address-card">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm text-[#1F1C18]">{a.label}</span>
                  {a.is_default && <span className="text-xs px-2 py-0.5 rounded-full bg-terracotta/10 text-terracotta font-medium flex items-center gap-1"><Star size={10} /> Par défaut</span>}
                </div>
                <p className="text-sm text-[#6B655C]">{a.first_name} {a.last_name}</p>
                <p className="text-sm text-[#6B655C]">{a.address}</p>
                <p className="text-sm text-[#6B655C]">{a.postal} {a.city}</p>
                {a.phone && <p className="text-sm text-[#6B655C]">{a.phone}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => startEdit(a)} className="p-2 text-[#6B655C] hover:text-terracotta" data-testid="address-edit-btn"><Edit size={16} /></button>
                <button onClick={() => remove(a.id)} className="p-2 text-[#6B655C] hover:text-red-500" data-testid="address-delete-btn"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SecurityTab() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", new_password_confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.new_password_confirm) return toast.error("Les mots de passe ne correspondent pas");
    if (form.new_password.length < 8) return toast.error("8 caractères minimum");
    setLoading(true);
    try {
      await api.put("/auth/password", form);
      toast.success("Mot de passe modifié. Un e-mail de confirmation a été envoyé.");
      setForm({ current_password: "", new_password: "", new_password_confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate("/"); toast.success("Déconnexion réussie"); };

  return (
    <div data-testid="security-tab">
      <h2 className="font-serif-display text-2xl mb-6">Sécurité et mot de passe</h2>
      <form onSubmit={submit} className="space-y-4 max-w-lg">
        <div>
          <label className="block text-sm text-[#6B655C] mb-1.5">Mot de passe actuel</label>
          <div className="relative">
            <input required type={showPw ? "text" : "password"} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} placeholder="Votre mot de passe actuel" className="w-full p-3 pr-11 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="current-password" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B655C] hover:text-terracotta">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm text-[#6B655C] mb-1.5">Nouveau mot de passe</label>
          <input required type={showPw ? "text" : "password"} minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} placeholder="8 caractères minimum" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="new-password" />
        </div>
        <div>
          <label className="block text-sm text-[#6B655C] mb-1.5">Confirmer le nouveau mot de passe</label>
          <input required type={showPw ? "text" : "password"} minLength={8} value={form.new_password_confirm} onChange={(e) => setForm({ ...form, new_password_confirm: e.target.value })} placeholder="Retapez le mot de passe" className="w-full p-3 rounded-2xl border border-[#E5E1D8] focus:outline-none focus:border-terracotta" data-testid="confirm-new-password" />
        </div>
        <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50" data-testid="change-password-btn">
          {loading ? "Modification..." : "Modifier le mot de passe"}
        </button>
      </form>
      <div className="mt-10 pt-6 border-t border-[#E5E1D8]">
        <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600 transition" data-testid="logout-btn">
          <LogOut size={16} /> Se déconnecter
        </button>
      </div>
    </div>
  );
}

export default function Account() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [authLoading, user, navigate]);

  if (authLoading || !user) return null;
  if (user.role === "admin") { navigate("/admin"); return null; }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="account-page">
      <p className="font-accent text-3xl text-terracotta -rotate-2 mb-2">Mon espace</p>
      <h1 className="font-serif-display text-4xl sm:text-5xl mb-2">Bonjour {user.first_name || user.name} ♡</h1>
      <p className="text-[#6B655C] mb-10">{user.email}</p>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar */}
        <nav className="lg:w-56 shrink-0">
          <div className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm whitespace-nowrap transition ${tab === t.key ? "bg-terracotta/10 text-terracotta font-medium" : "text-[#6B655C] hover:bg-[#f7f5f0]"}`}
                data-testid={`tab-${t.key}`}
              >
                <t.icon size={17} />
                {t.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 bg-white/70 backdrop-blur-md rounded-3xl p-6 sm:p-8 min-h-[400px]">
          {tab === "profile" && <ProfileTab />}
          {tab === "orders" && <OrdersTab />}
          {tab === "addresses" && <AddressesTab />}
          {tab === "security" && <SecurityTab />}
        </div>
      </div>
    </div>
  );
}
